import { Box, Typography, capitalize } from "@mui/material"
import { DeleteOutlined } from "@mui-symbols-material/w300"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { FC } from "react"

import { getWarehouseTypeLabel } from "../../utils"

import WarehouseCompaniesFilterChips from "./WarehouseCompaniesFilterChips"

import { Button } from "@/components/common/Button"
import { FilterChip } from "@/components/common/Filters"
import type { WarehouseAreaListFilter, WarehouseType } from "@/graphql/codegen/graphql"
import { useHandleClearFilters } from "@/screens/Warehouses/hooks/useHandleClearFilters"
import { WarehouseStatus } from "@/screens/Warehouses/types"

const WarehouseFilterChips: FC = () => {
  const { isActive, types, companyIds } = useSearch({
    from: "/app/admin/warehouses/",
    strict: true,
  })
  const navigate = useNavigate({ from: "/app/admin/warehouses" })
  const { onClear } = useHandleClearFilters()

  if (isActive === undefined && !types?.length && !companyIds?.length) {
    return null
  }

  const status = isActive ? WarehouseStatus.Active : WarehouseStatus.Inactive
  const label = capitalize(status.toLowerCase())

  const handleClearFilter = (name: keyof WarehouseAreaListFilter, value?: unknown) => {
    let searchReducer: (prev: Record<string, any>) => Record<string, any>

    if (name === "isActive") {
      searchReducer = (prev: Record<string, any>) => ({
        ...prev,
        [name]: undefined,
      })
    } else {
      searchReducer = (prev: Record<string, any>) => {
        const param = (prev[name] as string[]).filter((id) => id !== value)
        return {
          ...prev,
          [name]: Array.isArray(param) && param.length ? param : undefined,
        }
      }
    }

    navigate({
      search: searchReducer,
      replace: true,
    })
  }

  return (
    <Box className='mb-6 flex flex-wrap items-center gap-2' data-testid='warehouse-filter-chips'>
      <Typography variant='body1' className='text-gray-700'>
        Applied filters:
      </Typography>
      {isActive !== undefined && (
        <FilterChip
          key={`isActive-${isActive}`}
          label={label}
          onDelete={() => handleClearFilter("isActive")}
          testId='warehouse-status-filter-chip'
        />
      )}
      {types?.map((type: WarehouseType) => (
        <FilterChip
          key={`type-${type}`}
          label={getWarehouseTypeLabel(type)}
          onDelete={() => handleClearFilter("types", type)}
          testId={`warehouse-type-filter-chip-${type}`}
        />
      ))}
      {companyIds?.length && (
        <WarehouseCompaniesFilterChips
          selectedCompanies={companyIds}
          onRemove={(name, id) => handleClearFilter(name, id)}
        />
      )}
      <Button
        variant='primary'
        size='small'
        appearance='text'
        tabIndex={-1}
        startIcon={<DeleteOutlined fontSize='small' />}
        onClick={onClear}
      >
        Clear All
      </Button>
    </Box>
  )
}

export default WarehouseFilterChips
